<template>
    <div class="plan-picker">
        <!-- Visible error state when the plan list didn't arrive from the server.
             The wizard cannot score recommendations without real plan data, so
             we fail loudly here rather than run the algorithm against nothing. -->
        <div v-if="allPlans.length === 0" class="plan-picker__error">
            <q-icon name="cloud_off" size="40px" color="negative" class="q-mb-sm" />
            <div class="text-weight-bold q-mb-xs" style="font-size:1.1rem;color:#0a0f1a;">
                Plan data isn't available right now
            </div>
            <div class="text-grey-7 q-mb-md" style="font-size:0.9rem;line-height:1.5;max-width:440px;margin:0 auto;">
                The wizard needs live plan data to make an honest recommendation - we won't
                guess. Please refresh the page, or <a href="/Home/Support" class="text-primary">contact support</a>
                if this persists.
            </div>
            <q-btn flat no-caps size="sm" label="Refresh" icon="refresh" @click="reloadPage" />
        </div>

        <!-- Questions panel -->
        <div v-if="allPlans.length > 0" class="plan-picker__questions">
            <div v-for="(q, idx) in questions" :key="q.key" class="plan-picker__question q-mb-lg">
                <div class="text-weight-bold q-mb-sm" style="font-size:0.95rem;color:#0F172A;">
                    <span class="plan-picker__qnum">{{ idx + 1 }}</span>
                    {{ q.label }}
                </div>
                <div class="row q-col-gutter-sm">
                    <div v-for="opt in q.options" :key="opt.value"
                         class="col-auto">
                        <q-btn :outline="answers[q.key] !== opt.value"
                               :color="answers[q.key] === opt.value ? 'primary' : 'grey-8'"
                               :label="opt.label"
                               no-caps unelevated
                               size="sm"
                               style="border-radius:8px;"
                               @click="setAnswer(q.key, opt.value)" />
                    </div>
                </div>
            </div>
            <p class="text-grey-6 q-mt-lg" style="font-size:0.8rem;">
                Recommendations update live as you answer. No signup needed to see results.
            </p>
        </div>

        <!-- Recommendations panel -->
        <div v-if="allPlans.length > 0" class="plan-picker__results q-mt-xl">
            <div class="text-center q-mb-lg">
                <div class="text-weight-bold q-mb-xs" style="font-size:0.85rem;color:#6366F1;text-transform:uppercase;letter-spacing:1px;">
                    {{ answersComplete ? 'Based on your answers' : 'Our best guess so far' }}
                </div>
                <h3 class="text-weight-bold q-mb-xs" style="font-size:1.6rem;color:#0a0f1a;">
                    <template v-if="recommendations.length === 1">
                        We recommend {{ recommendations[0].plan.name }}
                    </template>
                    <template v-else-if="recommendations.length > 1">
                        Top {{ recommendations.length }} fits for your needs
                    </template>
                    <template v-else>
                        No plans matched - adjust your answers
                    </template>
                </h3>
                <p v-if="topReason" class="text-grey-7" style="font-size:0.95rem;max-width:600px;margin:0 auto;">{{ topReason }}</p>
            </div>

            <div v-if="recommendations.length > 0" class="row q-col-gutter-lg justify-center">
                <div v-for="(rec, i) in recommendations"
                     :key="rec.plan.tierCode"
                     :class="recommendations.length === 1 ? 'col-12 col-md-6' : 'col-12 col-sm-6 col-md-4'">
                    <!-- Show which semantic group this recommendation belongs to
                         so the user sees e.g. 'From: Developers &amp; AI Agents' -
                         reinforces the page's grouped navigation mental model. -->
                    <div class="text-center q-mb-sm">
                        <span class="text-caption text-weight-medium text-grey-7"
                              style="text-transform:uppercase;letter-spacing:0.5px;">
                            From {{ groupLabelFor(rec.plan) }}
                        </span>
                    </div>
                    <plan-card :tier-code="rec.plan.tierCode"
                               :featured="i === 0"
                               :featured-label="i === 0 ? 'Best fit' : null" />
                </div>
            </div>

            <div v-else class="text-center q-py-xl">
                <q-icon name="search_off" size="48px" color="grey-5" />
                <p class="text-grey-7 q-mt-md">Try relaxing a requirement - most teams don't need every plan feature.</p>
            </div>
        </div>
    </div>
</template>

<script>
/**
 * PlanPickerWizard - interactive tier-recommendation wizard.
 *
 * The user answers 5 simple multiple-choice questions; the algorithm scores
 * every plan in _pageConfig.plans against the answers and returns the top 2-3.
 * Hard requirements (e.g., "yes I need SSO") exclude plans that don't support
 * them; soft preferences (volume, team size) add to a fit score.
 *
 * Scoring is data-driven. Adding a new plan to Sign_SubscriptionPlans with
 * appropriate flags picks it up automatically - no hardcoded tier logic.
 *
 * The component is single-panel (no multi-step) so the user sees the
 * recommendation update live as they click. This is the core UX selling point
 * - it feels like exploring, not filling out a form.
 */
import PlanCard from './PlanCard.vue';
import { getPlans, getGroupLabel } from './planData.js';

const HARD_REQUIRED = 'hardRequired';
const STRONG_PREFERENCE = 'strongPreference';

export default {
    name: 'PlanPickerWizard',
    components: { PlanCard },
    props: {
        /** How many top-ranked plans to display. Defaults to 2. */
        maxRecommendations: { type: Number, default: 2 }
    },
    data() {
        return {
            questions: [
                {
                    key: 'volume',
                    label: 'About how many contracts do you send per month?',
                    options: [
                        { value: 'adhoc', label: 'Just a few (0-5)' },
                        { value: 'low', label: '5-25' },
                        { value: 'mid', label: '25-100' },
                        { value: 'high', label: '100-500' },
                        { value: 'veryHigh', label: '500+' }
                    ]
                },
                {
                    key: 'teamSize',
                    label: 'How many people on your team will use it?',
                    options: [
                        { value: 'solo', label: 'Just me' },
                        { value: 'small', label: '2-5' },
                        { value: 'mid', label: '6-15' },
                        { value: 'large', label: '16-50' },
                        { value: 'huge', label: '50+' }
                    ]
                },
                {
                    key: 'payments',
                    label: 'Do you need to collect payments during signing?',
                    options: [
                        { value: 'essential', label: 'Yes, that\'s the point' },
                        { value: 'sometimes', label: 'Sometimes' },
                        { value: 'never', label: 'No' }
                    ]
                },
                {
                    key: 'api',
                    label: 'Do you need to embed signing in your own app?',
                    options: [
                        { value: 'essential', label: 'Yes, API-first' },
                        { value: 'maybe', label: 'Maybe later' },
                        { value: 'no', label: 'No' }
                    ]
                },
                {
                    key: 'sso',
                    label: 'Do you need SSO / SCIM (Okta, Entra ID, etc.)?',
                    options: [
                        { value: 'yes', label: 'Yes' },
                        { value: 'no', label: 'No' }
                    ]
                }
            ],
            answers: {
                volume: null,
                teamSize: null,
                payments: null,
                api: null,
                sso: null
            }
        };
    },
    computed: {
        allPlans() {
            return getPlans();
        },
        answersComplete() {
            return Object.values(this.answers).every(v => v !== null);
        },
        /**
         * Return scored + filtered plans, ordered best-fit first.
         * Unlisted tierCodes like unknown or test plans are skipped via the
         * `sortOrder >= 0` check.
         */
        recommendations() {
            const hardFiltered = this.allPlans.filter(p => this.passesHardRequirements(p));
            const scored = hardFiltered.map(plan => ({
                plan,
                score: this.scorePlan(plan),
                reasons: this.buildReasons(plan)
            }));
            scored.sort((a, b) => b.score - a.score);
            return scored.slice(0, this.maxRecommendations);
        },
        topReason() {
            if (!this.answersComplete) return 'Answer the questions above to refine the recommendation.';
            if (this.recommendations.length === 0) return '';
            return this.recommendations[0].reasons.join(' · ');
        }
    },
    methods: {
        setAnswer(key, value) {
            // Toggle off if clicking the same value twice, otherwise set.
            this.answers[key] = this.answers[key] === value ? null : value;
        },

        reloadPage() {
            if (typeof window !== 'undefined') {
                window.location.reload();
            }
        },

        groupLabelFor(plan) {
            return getGroupLabel(plan?.planGroup);
        },

        passesHardRequirements(plan) {
            const a = this.answers;

            // SSO is the clearest hard requirement. If user says yes, the
            // plan MUST support it; otherwise we're wasting their time.
            if (a.sso === 'yes' && !plan.allowSsoScim) return false;

            // API "essential" hard-filters out plans without API access.
            if (a.api === 'essential' && !plan.allowApiAccess) return false;

            // Payments "essential" hard-filters out plans that don't collect payments.
            if (a.payments === 'essential' && !plan.allowPaymentCollection) return false;

            return true;
        },

        /**
         * Scoring function. Higher score = better fit. Weights reflect what
         * customer feedback suggests matters most - signing volume and
         * team size are the big drivers; feature flags are tiebreakers.
         */
        scorePlan(plan) {
            const a = this.answers;
            let score = 50; // baseline so every plan starts at a neutral score

            // ---- Volume fit ----
            // The closer the plan's packageLimit is to the answer, the better.
            // Too-low is worse than too-high (overage on a plan that doesn't
            // match your volume is a bad experience).
            const answeredVolume = {
                adhoc: 3, low: 20, mid: 75, high: 300, veryHigh: 1500
            }[a.volume];
            if (answeredVolume != null) {
                const capacity = plan.packageLimit > 0 ? plan.packageLimit : 1; // Free PAYG
                if (plan.tierCode === 'free' && a.volume === 'adhoc') {
                    score += 30; // Free tier is literally built for ad-hoc
                } else if (capacity >= answeredVolume) {
                    // Plan covers the volume - bonus decreases as capacity gets too big.
                    const overshoot = capacity / answeredVolume;
                    score += overshoot > 10 ? 5 : overshoot > 3 ? 15 : 25;
                } else {
                    // Plan is under-sized. Penalize proportionally.
                    const shortfall = answeredVolume / capacity;
                    score -= Math.min(40, shortfall * 10);
                }
            }

            // ---- Team size fit ----
            const answeredSeats = {
                solo: 1, small: 4, mid: 10, large: 30, huge: 75
            }[a.teamSize];
            if (answeredSeats != null) {
                if (plan.tierCode === 'developer' && answeredSeats > 1) {
                    score -= 40; // single-dev tier is wrong for a team
                } else if (plan.userLimit !== -1 && plan.userLimit > 0 && plan.userLimit < answeredSeats) {
                    score -= 30; // hard cap is below what they need
                } else if (plan.userLimit === -1 && answeredSeats > 15) {
                    score += 15; // unlimited plan rewarded for big teams
                } else if (plan.baseUserCount === answeredSeats) {
                    score += 20; // the exact included-seats match
                } else if (answeredSeats <= plan.baseUserCount) {
                    score += 10; // fits inside base, no per-seat cost
                } else {
                    // Per-user charges apply - not a fit-killer but costs more.
                    score -= 5;
                }
            }

            // ---- Payment collection preference ----
            if (a.payments === 'essential' && plan.allowPaymentCollection) {
                // Prefer the tier that ONLY does payments (Collect) over Pro/Business
                // if the user said it's the point.
                if (plan.tierCode === 'collect') score += 25;
                else score += 10;
            } else if (a.payments === 'sometimes' && plan.allowPaymentCollection) {
                score += 8;
            } else if (a.payments === 'never' && plan.allowPaymentCollection) {
                score -= 2; // minor - they just don't care
            }

            // ---- API preference ----
            if (a.api === 'essential' && plan.allowApiAccess) {
                if (plan.tierCode === 'developer') score += 25;
                else score += 10;
            } else if (a.api === 'maybe' && plan.allowApiAccess) {
                score += 5;
            } else if (a.api === 'no' && plan.tierCode === 'developer') {
                score -= 30; // Developer tier is overkill for non-API users
            }

            // ---- SSO preference ----
            if (a.sso === 'yes' && plan.allowSsoScim) {
                score += 20;
            } else if (a.sso === 'no' && plan.allowSsoScim) {
                // Business is overpriced for a user who doesn't need SSO.
                score -= 8;
            }

            return score;
        },

        buildReasons(plan) {
            const a = this.answers;
            const out = [];
            if (a.volume === 'adhoc' && plan.tierCode === 'free') {
                out.push('Pay-as-you-go matches low-volume use');
            } else if (plan.packageLimit > 0 && plan.packageLimit >= 100) {
                out.push(`${plan.packageLimit} signings included`);
            }
            if (a.sso === 'yes' && plan.allowSsoScim) out.push('SSO + SCIM included');
            if (a.api === 'essential' && plan.allowApiAccess) out.push('Full API access');
            if (a.payments === 'essential' && plan.allowPaymentCollection) {
                const pct = (plan.platformFeePercent * 100).toFixed(plan.platformFeePercent < 0.01 ? 2 : 1);
                out.push(`Payment collection (${pct}% fee)`);
            }
            if (a.teamSize === 'huge' && plan.userLimit === -1) out.push('Unlimited users');
            return out;
        }
    }
};
</script>

<style scoped>
.plan-picker {
    max-width: 900px;
    margin: 0 auto;
}
.plan-picker__qnum {
    display: inline-block;
    width: 22px;
    height: 22px;
    line-height: 22px;
    text-align: center;
    border-radius: 50%;
    background: #EEF2F7;
    color: #6366F1;
    font-size: 0.75rem;
    font-weight: 700;
    margin-right: 6px;
}
.plan-picker__error {
    text-align: center;
    padding: 48px 24px;
    border: 1px dashed #FCA5A5;
    background: #FEF2F2;
    border-radius: 12px;
    max-width: 560px;
    margin: 0 auto;
}
</style>
